import { useRef, useState } from 'react';
import { useT, useUiStore } from '@/store';
import { useBulkOrderAction, useLiveEta, useOrders, useSetOrderStatus, useUpdateOrder } from '@/hooks/useOrders';
import { useAllQuarries, useDrivers } from '@/hooks/useDirectory';
import { useDwellStatus } from '@/hooks/useTenant';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { StatusBadge, DwellBadge, NotNotifiedBadge } from '@/components/ui/StatusBadge';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { formatOrderDateTime, waybillLabel } from '@/lib/format';
import { deliveryWindow, windowLabel } from '@/lib/deliveryWindow';
import { OCRInspectionModal } from './OCRInspectionModal';

const PAGE_SIZES = [10, 25, 50, 100];
const TERMINAL = new Set(['delivered', 'cancelled']);

/**
 * The orders table: server-paginated, inline driver reassignment, bulk
 * cancel, and a row click that opens the waybill/OCR inspector.
 *
 * Selection is keyed by order id and cleared whenever the page or filters
 * change. The monolith cleared it on every re-render, including the ones
 * caused by a background live update — so a dispatcher who had ticked six
 * rows could watch the selection vanish mid-task because an unrelated GPS
 * ping arrived.
 */
/**
 * V-99. The delivery window the client asked for, on the row where a
 * dispatcher looks at the load.
 *
 * Under the order's timestamp rather than in a column of its own: most loads
 * carry no window at all (a dispatcher-typed order never does), and a column
 * that is empty on four rows in five is a column that stops being read — and
 * takes the fifth row with it.
 *
 * Nothing at all when there is no promise. Silence here means "this client
 * asked for no particular time", which is the truth, and is different from a
 * dash — a dash reads as a missing value somebody should go and fill in.
 */
function DeliveryWindowLine({ order, eta }) {
  const t = useT();
  const window = deliveryWindow(order, new Date(), eta);
  if (!window) return null;

  const range = windowLabel(window);
  const line = {
    open: () => t('orders.windowOpen', { range }),
    closing: () => t('orders.windowClosing', { n: window.minutesLeft, range }),
    // The forecast, and the only row state that is still fixable. See
    // willMiss() in lib/deliveryWindow.js.
    will_miss: () => t('orders.windowWillMiss', { n: window.minutesLate, range }),
    missed: () => t('orders.windowMissed', { n: window.minutesLate }),
    kept: () => t('orders.windowKept', { range }),
    late: () => t('orders.windowLate', { n: window.minutesLate, range }),
    early: () => t('orders.windowEarly', { range }),
  }[window.state];

  // 'early' is deliberately not coloured: a truck that arrived before the
  // window is a site that may not be ready to receive it, which is worth
  // knowing and is nobody's fault. Red is kept for the two states that are.
  const tone = {
    missed: 'text-[#FCA5A5] font-semibold',
    late: 'text-[#FCA5A5] font-semibold',
    // As loud as a missed window, deliberately: the difference between the
    // two is only that this one can still be prevented.
    will_miss: 'text-[#FCA5A5] font-semibold',
    closing: 'text-amber font-semibold',
    kept: 'text-green',
  }[window.state] ?? 'text-dim';

  return <span className={`block text-[10.5px] ${tone}`}>{line()}</span>;
}

export function OrdersTable({ filters, onFiltersChange }) {
  const t = useT();
  const lang = useUiStore((s) => s.lang);
  const { data, isLoading, isError, error, isPlaceholderData } = useOrders(filters);
  const { data: drivers = [] } = useDrivers();
  const { data: dwell } = useDwellStatus();
  const dwellByOrder = dwell?.byOrder ?? {};
  // Shares the live strip's cache entry — same key, same 60 s poll, no
  // second round of routing calls for having two screens open.
  const { data: etaByOrder = {} } = useLiveEta();

  const updateOrder = useUpdateOrder();
  const setStatus = useSetOrderStatus();
  const bulkAction = useBulkOrderAction();

  const [selected, setSelected] = useState(() => new Set());
  const [inspectingOrderId, setInspectingOrderId] = useState(null);
  const [rowError, setRowError] = useState('');
  const [bulkNotice, setBulkNotice] = useState(null);

  // Roving tabindex over the rows (Phase 9.2). The row click that opens the
  // inspector was mouse-only: Tab reached the checkbox, the driver picker
  // and the buttons inside a row, but never the row itself, so the shortcut
  // simply did not exist without a pointer.
  //
  // One tab stop for the whole body rather than one per row — a 100-row page
  // would otherwise put a hundred stops between the table and the pager.
  // Arrows move between rows, Enter opens the waybill, Home/End jump to the
  // ends of the page.
  const [focusedRow, setFocusedRow] = useState(0);
  const rowRefs = useRef([]);

  const orders = data?.orders ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = data?.total_pages ?? 1;
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 25;
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + orders.length;

  const clearSelection = () => setSelected(new Set());

  const toggleRow = (orderId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });

  const changeFilters = (patch) => {
    clearSelection();
    setFocusedRow(0);
    onFiltersChange(patch);
  };

  // Clamped rather than reset on every data change: a live update that
  // shortens the page should not throw the keyboard back to row one, and an
  // index left pointing past the end would leave the table with no tab stop
  // at all.
  const activeRow = orders.length ? Math.min(focusedRow, orders.length - 1) : 0;

  const moveFocus = (index) => {
    const next = Math.max(0, Math.min(index, orders.length - 1));
    setFocusedRow(next);
    rowRefs.current[next]?.focus();
  };

  const onRowKeyDown = (event, index, orderId) => {
    // A key pressed while focus sits in the driver picker belongs to that
    // picker — arrows change its value there, and it bubbles up here. Only
    // keys aimed at the row itself are the row's.
    if (event.target !== event.currentTarget) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(orders.length - 1);
        break;
      case 'Enter':
      case ' ':
        // Space would scroll the panel out from under the dispatcher.
        event.preventDefault();
        setInspectingOrderId(orderId);
        break;
      default:
        break;
    }
  };

  const cancelOne = (orderId) => {
    setRowError('');
    setStatus.mutate(
      { orderId, status: 'cancelled' },
      { onError: (err) => setRowError(`${t('common.error')}: ${err.message}`) },
    );
  };

  const reassign = (orderId, driverId) => {
    setRowError('');
    updateOrder.mutate(
      { orderId, driver_id: driverId },
      { onError: (err) => setRowError(`${t('common.error')}: ${err.message}`) },
    );
  };

  /**
   * One request for the whole selection — see useBulkOrderAction().
   *
   * The refusal is the interesting path and it is handled explicitly: the
   * server checks every ticked row before it touches any of them, so a 409
   * here means NOTHING was changed and the body says which rows blocked it.
   * Showing that list is the difference between "try again" and "untick
   * these three and try again".
   */
  const runBulk = (body) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setRowError('');
    setBulkNotice(null);
    bulkAction.mutate(
      { ...body, order_ids: ids },
      {
        onSuccess: (result) => {
          clearSelection();
          setBulkNotice(result);
        },
        onError: (err) => {
          const problems = err.body?.problems ?? [];
          setRowError(
            problems.length
              ? `${err.message} (${problems
                  .map((p) => `${p.order_id.slice(0, 6)} — ${t(`orders.bulkProblem.${p.reason}`)}`)
                  .join('; ')})`
              : `${t('common.error')}: ${err.message}`,
          );
        },
      },
    );
  };

  return (
    <Panel title={t('nav.orders')}>
      {selected.size > 0 ? (
        <BulkActionBar
          count={selected.size}
          pending={bulkAction.isPending}
          onRun={runBulk}
          onClear={clearSelection}
        />
      ) : null}
      {isError ? <ErrorState error={error} /> : null}
      {rowError ? <ErrorState>{rowError}</ErrorState> : null}
      {bulkNotice ? <BulkResultNotice result={bulkNotice} onDismiss={() => setBulkNotice(null)} /> : null}

      {isLoading ? (
        <SkeletonRows rows={6} />
      ) : orders.length === 0 ? (
        <EmptyState>{t('orders.noneMatchFilters')}</EmptyState>
      ) : (
        <div className={`overflow-x-auto transition-opacity ${isPlaceholderData ? 'opacity-60' : ''}`}>
          <table className="w-full border-collapse text-[12px]" aria-label={t('nav.orders')}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.03em] text-dim">
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    aria-label={t('orders.selectAll')}
                    checked={selected.size > 0 && selected.size === orders.length}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(orders.map((o) => o.order_id)) : new Set())
                    }
                  />
                </th>
                <th className="px-2 py-2">{t('orders.colId')}</th>
                <th className="px-2 py-2">{t('orders.colDateTime')}</th>
                <th className="px-2 py-2">{t('orders.colClientSite')}</th>
                <th className="px-2 py-2">{t('orders.colQuarry')}</th>
                <th className="px-2 py-2">{t('orders.colCargoWeight')}</th>
                <th className="px-2 py-2">{t('orders.colDriver')}</th>
                <th className="px-2 py-2">{t('orders.colStatus')}</th>
                <th className="px-2 py-2">{t('orders.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => {
                const terminal = TERMINAL.has(order.order_status);
                return (
                  <tr
                    key={order.order_id}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    tabIndex={index === activeRow ? 0 : -1}
                    onKeyDown={(e) => onRowKeyDown(e, index, order.order_id)}
                    // Keeps the single tab stop where the dispatcher last
                    // was, however they got there — arrows, or a click.
                    onFocus={() => setFocusedRow(index)}
                    className="cursor-pointer border-t border-line hover:bg-white/3 focus:outline-2 focus:-outline-offset-2 focus:outline-accent"
                    onClick={(e) => {
                      // Let the checkbox, the driver select and the action
                      // buttons keep their own handlers — without this the
                      // click bubbles and opens the inspector on top of an
                      // interaction that was not asking for it.
                      if (e.target.closest('input, select, button, a')) return;
                      setInspectingOrderId(order.order_id);
                    }}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(order.order_id)}
                        onChange={() => toggleRow(order.order_id)}
                        aria-label={waybillLabel(order)}
                      />
                    </td>
                    <td className="px-2 py-2 font-semibold">{waybillLabel(order)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-dim">
                      {formatOrderDateTime(order.created_at, lang)}
                      <DeliveryWindowLine order={order} eta={etaByOrder[order.order_id]} />
                    </td>
                    {/* The joins behind these two names are deliberately not
                        filtered on `active` — that is what keeps an old order
                        readable after its site or quarry is archived. The badge
                        is the other half of that bargain: the name resolves,
                        and the dispatcher is told it is a legacy record rather
                        than being sent to look for something that is gone. */}
                    <td className="px-2 py-2">
                      {[order.client_name, order.site_name].filter(Boolean).join(' · ') || '—'}
                      {order.site_id && order.site_active === 0 ? (
                        <ArchivedBadge deletedAt={order.site_deleted_at} />
                      ) : null}
                    </td>
                    <td className="px-2 py-2">
                      {order.quarry_name || '—'}
                      {order.quarry_id && order.quarry_active === 0 ? (
                        <ArchivedBadge deletedAt={order.quarry_deleted_at} />
                      ) : null}
                    </td>
                    <td className="px-2 py-2">
                      <div>{order.cargo_type}</div>
                      <div className="text-[10.5px] text-dim">
                        {order.actual_ocr_weight_tons
                          ? `${order.actual_ocr_weight_tons}t ${
                              order.waybill_source === 'manual' ? `(${t('common.manually')})` : 'OCR'
                            }`
                          : `${order.estimated_weight_tons ?? '—'}t ${t('common.estSuffix')}`}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <select
                        className="field !w-auto !py-1 !text-[11px]"
                        aria-label={t('orders.driverForOrder', { id: waybillLabel(order) })}
                        value={order.driver_id ?? ''}
                        disabled={terminal}
                        onChange={(e) => reassign(order.order_id, e.target.value)}
                      >
                        <option value="">{t('dispatcher.unassigned')}</option>
                        {/* V-368. The same option OrderForm.jsx has carried
                            since its own `pickedNotListed`, and for the same
                            reason it states there: "without an option to hold
                            it, the select silently falls back ... which is two
                            controls showing one value as two different
                            answers". This picker never got one, though its id
                            outlives the roster for a far more certain reason
                            than the form's race between two fetches — the
                            driver left the company, and a delivered load keeps
                            its driver_id. With nothing to match, the control
                            fell back to its first option and read
                            "Nepriskirtas": a wrong fact, not a missing badge,
                            on the same row where the quarry beside it is
                            correctly marked "(Archyvuota)". driver_name was in
                            the payload all along; only the select never read
                            it. Rendered from the order rather than the roster,
                            so it also covers the first paint, while
                            useDrivers() is still on its placeholder []. The
                            marker is the one thing the form's case does not
                            need: that one is transient, this one is permanent,
                            so the row has to say WHY the name is off-roster —
                            which is what driver_active is for. */}
                        {order.driver_id
                        && !drivers.some((d) => d.driver_id === order.driver_id) ? (
                          <option value={order.driver_id}>
                            {order.driver_name
                              ? `${order.driver_name}${
                                order.driver_active === 0 ? ` (${t('common.archived')})` : ''
                              }`
                              : t('dispatcher.unassigned')}
                          </option>
                          ) : null}
                        {drivers.map((d) => (
                          <option key={d.driver_id} value={d.driver_id}>
                            {d.full_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">
                      <StatusBadge status={order.order_status} />
                      <DwellBadge entry={dwellByOrder[order.order_id]} />
                      {/* "Išsiųstas" and "the driver knows" are two different
                          facts. This is the second one. */}
                      <NotNotifiedBadge order={order} />
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          className="btn !px-2.5 !py-1 !text-[11px]"
                          onClick={() => setInspectingOrderId(order.order_id)}
                        >
                          {t('orders.waybillBtn')}
                        </button>
                        {!terminal ? (
                          <ConfirmButton onConfirm={() => cancelOne(order.order_id)} label={t('common.cancel')} />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <footer className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[11px] text-dim">
        <span>{t('orders.showingRange', { from, to, total: totalCount })}</span>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5">
            {t('orders.perPage')}
            <select
              className="field !w-auto !py-1"
              value={pageSize}
              onChange={(e) => changeFilters({ pageSize: Number(e.target.value), page: 1 })}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn" disabled={page <= 1} onClick={() => changeFilters({ page: page - 1 })}>
            {t('orders.prevPage')}
          </button>
          <span>
            {page} {t('common.of')} {Math.max(totalPages, 1)}
          </span>
          <button
            type="button"
            className="btn"
            disabled={page >= totalPages}
            onClick={() => changeFilters({ page: page + 1 })}
          >
            {t('orders.nextPage')}
          </button>
        </div>
      </footer>

      <OCRInspectionModal orderId={inspectingOrderId} onClose={() => setInspectingOrderId(null)} />
    </Panel>
  );
}

/**
 * Masinis kelių užsakymų perkėlimas / atšaukimas vienu veiksmu.
 *
 * THE SCENARIO, because the shape of this bar follows from it: a quarry
 * closes at 09:00 with fourteen loads booked out of it. What a dispatcher
 * needs is not fourteen edits — it is one decision, applied to the
 * selection, with the drivers told. Before this, the console could only
 * cancel a selection (as a client-side loop, one request per row) and had
 * no move at all, so the closure meant cancelling fourteen loads and
 * retyping them against the next quarry: fourteen new orders, every agreed
 * rate and client reference entered again.
 *
 * THE REASON FIELD IS REQUIRED, and the server enforces it rather than
 * trusting this component. It is written into every affected order's own
 * history: this is the one screen in the product that changes many loads at
 * once with no per-load confirmation, and three weeks later "why was the
 * whole morning moved to Kalvarija" has to be answerable from the order,
 * not from whoever was on shift.
 *
 * The move is armed only once a destination is picked, and cancel keeps the
 * two-step ConfirmButton the per-row action already uses — a selection of
 * fourteen is more than a single stray click should be able to end, not
 * less.
 */
function BulkActionBar({ count, pending, onRun, onClear }) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [quarryId, setQuarryId] = useState('');
  const { data: quarries = [] } = useAllQuarries();
  const canRun = reason.trim().length > 0 && !pending;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[11px] border border-accent/40 bg-accent/5 p-2.5">
      <span className="text-[11px] font-semibold">{t('orders.selectedCount', { n: count })}</span>

      <input
        className="field !w-auto !py-1 !text-[11px] grow min-w-[180px]"
        placeholder={t('orders.bulkReasonPlaceholder')}
        aria-label={t('orders.bulkReason')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />

      <select
        className="field !w-auto !py-1 !text-[11px]"
        aria-label={t('orders.bulkMoveToQuarry')}
        value={quarryId}
        onChange={(e) => setQuarryId(e.target.value)}
      >
        <option value="">{t('orders.bulkMoveToQuarry')}</option>
        {quarries.map((q) => (
          <option key={q.quarry_id} value={q.quarry_id}>
            {q.name}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="btn !px-2.5 !py-1 !text-[11px]"
        disabled={!canRun || !quarryId}
        onClick={() => onRun({ action: 'move', quarry_id: quarryId, reason: reason.trim() })}
      >
        {t('orders.bulkMove')}
      </button>

      {/* Same two-step arm-then-commit as the per-row cancel, and disabled
          until a reason is typed — the server refuses a reasonless batch
          anyway, and a button that 400s is worse than one that waits. */}
      <ConfirmButton
        onConfirm={() => onRun({ action: 'cancel', reason: reason.trim() })}
        label={t('orders.cancelSelected')}
        disabled={!canRun}
      />

      <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={onClear}>
        {t('orders.bulkClearSelection')}
      </button>
    </div>
  );
}

/**
 * What the batch actually did, in the three terms that can differ from what
 * was ticked.
 *
 * `unchanged` is the one worth rendering and the easy one to leave out: an
 * order already sitting at the destination is neither moved nor failed, and
 * a bar that says "14 moved" when two of them were already there is a bar
 * that overstates the system's own work. `pricing_review_needed` is the
 * other: a per_km or ton_km load carries a distance frozen against the OLD
 * quarry, and the server deliberately does not re-price it — a rate a
 * dispatcher agreed is not a bulk endpoint's to rewrite — so the only
 * honest thing left is to say which loads need looking at.
 */
function BulkResultNotice({ result, onDismiss }) {
  const t = useT();
  const applied = result.applied?.length ?? 0;
  const unchanged = result.unchanged?.length ?? 0;
  const review = result.pricing_review_needed?.length ?? 0;
  const notified = result.drivers_notified?.length ?? 0;

  return (
    <div role="status" className="mb-3 rounded-[11px] border border-line bg-white/3 p-2.5 text-[11px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          {result.action === 'move'
            ? t('orders.bulkMoved', { n: applied, notified })
            : t('orders.bulkCancelled', { n: applied })}
          {unchanged > 0 ? ` · ${t('orders.bulkUnchanged', { n: unchanged })}` : ''}
        </span>
        <button type="button" className="btn !px-2 !py-0.5 !text-[10px]" onClick={onDismiss}>
          {t('common.closeBtn')}
        </button>
      </div>
      {review > 0 ? (
        <p className="mt-1 text-amber">{t('orders.bulkPricingReview', { n: review })}</p>
      ) : null}
      {result.failed?.length ? (
        <p className="mt-1 text-[#FCA5A5]">
          {t('orders.bulkStopped', { id: result.failed[0].order_id.slice(0, 6) })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Two-step destructive action, carried over from the monolith's
 * confirmClick(): the first click arms the button, the second commits, and
 * it disarms itself after four seconds. Cancelling an order sends a driver a
 * message and cannot be undone from this screen, which is more than a
 * single stray click should be able to do — and a native confirm() is
 * blocked in several of the contexts this gets demoed in.
 */
function ConfirmButton({ onConfirm, label, disabled = false }) {
  const t = useT();
  const [armed, setArmed] = useState(false);

  const handleClick = () => {
    if (!armed) {
      setArmed(true);
      window.setTimeout(() => setArmed(false), 4000);
      return;
    }
    setArmed(false);
    onConfirm();
  };

  return (
    <button
      type="button"
      className="btn btn-danger !px-2.5 !py-1 !text-[11px]"
      disabled={disabled}
      onClick={handleClick}
    >
      {armed ? t('orders.confirmCancel') : label}
    </button>
  );
}
